import {
  jalaliMonthRange,
  type OrganizationProfitSummary,
  type ProfitDayRow,
  type ProfitFoodRow,
  type ProfitPeriodSummary,
} from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, formatDateShort, formatMoney, formatWeekday, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api, getProfitReport, type TreeNode } from "../../api.js";

type ViewMode = "by-org" | "daily";

export function ProfitPage() {
  const defaultRange = useMemo(() => jalaliMonthRange(), []);
  const [from, setFrom] = useState(defaultRange.startIso);
  const [to, setTo] = useState(defaultRange.endIso);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("by-org");

  // Load organizations for filter dropdown
  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api<TreeNode[]>("/organizations"),
  });

  const orgList = useMemo(() => {
    const list: { id: string; name: string }[] = [];
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        list.push({ id: n.id, name: n.name });
        walk(n.children);
      }
    };
    if (orgsQuery.data) walk(orgsQuery.data);
    return list;
  }, [orgsQuery.data]);

  // Load profit report
  const profitQuery = useQuery({
    queryKey: ["profit-report", from, to, selectedOrgId],
    queryFn: () =>
      getProfitReport({
        from: from || undefined,
        to: to || undefined,
        organizationId: selectedOrgId || undefined,
      }),
  });

  const report = profitQuery.data;

  return (
    <div className="stack" style={{ gap: "var(--s5)" }}>
      {/* Header and Controls */}
      <div
        className="row wrap"
        style={{ alignItems: "center", justifyContent: "space-between", gap: "var(--s4)" }}
      >
        <div>
          <h1 className="t-title" style={{ margin: 0 }}>
            گزارش سود و زیان (سازمان × غذا و روزانه)
          </h1>
          <p className="muted" style={{ margin: 0, fontSize: "13px" }}>
            محاسبه سود بر مبنای قیمت منجمد فاکتور و بهای تمام‌شده منجمد کالا (مدیریت)
          </p>
        </div>

        <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <Seg<ViewMode>
            label="نمای گزارش سود"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: "by-org", label: "سازمان × غذا" },
              { value: "daily", label: "روزانه" },
            ]}
          />
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <div className="row wrap" style={{ alignItems: "flex-end", gap: "var(--s4)" }}>
          <div className="field" style={{ minWidth: "160px" }}>
            <label htmlFor="profit-from">از تاریخ</label>
            <input
              id="profit-from"
              type="date"
              className="input"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
          </div>

          <div className="field" style={{ minWidth: "160px" }}>
            <label htmlFor="profit-to">تا تاریخ</label>
            <input
              id="profit-to"
              type="date"
              className="input"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          </div>

          <div className="field" style={{ minWidth: "200px" }}>
            <label htmlFor="profit-org">فیلتر سازمان</label>
            <select
              id="profit-org"
              className="input"
              value={selectedOrgId}
              onChange={(e) => {
                setSelectedOrgId(e.target.value);
              }}
            >
              <option value="">همه سازمان‌ها</option>
              {orgList.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ paddingBottom: "2px" }}>
            <Button
              variant="default"
              onClick={() => {
                const r = jalaliMonthRange();
                setFrom(r.startIso);
                setTo(r.endIso);
                setSelectedOrgId("");
              }}
            >
              بازنشانی به ماه جاری
            </Button>
          </div>
        </div>
      </Card>

      {/* Loading & Error States */}
      {profitQuery.isLoading && (
        <Card>
          <p className="muted text-center" style={{ padding: "var(--s6) 0" }}>
            در حال محاسبه و بارگذاری گزارش سود و زیان...
          </p>
        </Card>
      )}

      {profitQuery.isError && (
        <div className="alert urgent" role="alert">
          <div>
            <div className="b">خطا در دریافت گزارش سود</div>
            <p>{profitQuery.error instanceof Error ? profitQuery.error.message : "خطای ناشناخته رخ داد."}</p>
          </div>
        </div>
      )}

      {/* Content when report is loaded */}
      {report && (
        <>
          {/* Period Summary KPI Cards */}
          <PeriodSummaryCards summary={report.totalSummary} />

          {/* View Mode: Organization x Food */}
          {viewMode === "by-org" && (
            <OrganizationFoodTable rows={report.rows} summary={report.totalSummary} />
          )}

          {/* View Mode: Daily Gross Profit */}
          {viewMode === "daily" && <DailyGrossProfitTable days={report.days} summary={report.totalSummary} />}

          {/* Organization Summaries section */}
          {report.organizationSummaries.length > 0 && (
            <OrganizationSummariesGrid summaries={report.organizationSummaries} />
          )}
        </>
      )}
    </div>
  );
}

function PeriodSummaryCards({ summary }: { summary: ProfitPeriodSummary }) {
  const isLoss = summary.totalProfitRial < 0;

  return (
    <div className="row wrap" style={{ gap: "var(--s4)" }}>
      <div className="card stack" style={{ flex: "1 1 180px", gap: "var(--s1)" }}>
        <span className="muted" style={{ fontSize: "12px" }}>
          کل پرس دوره
        </span>
        <span className="bold" style={{ fontSize: "1.3rem" }}>
          {faDigits(summary.totalPortions)}
        </span>
      </div>

      <div className="card stack" style={{ flex: "1 1 180px", gap: "var(--s1)" }}>
        <span className="muted" style={{ fontSize: "12px" }}>
          کل فروش دوره
        </span>
        <span className="bold" style={{ fontSize: "1.3rem" }}>
          {formatMoney(summary.totalRevenueRial, "toman")}
        </span>
      </div>

      <div className="card stack" style={{ flex: "1 1 180px", gap: "var(--s1)" }}>
        <span className="muted" style={{ fontSize: "12px" }}>
          کل بهای تمام‌شده دوره
        </span>
        <span className="bold" style={{ fontSize: "1.3rem" }}>
          {formatMoney(summary.totalCostRial, "toman")}
        </span>
      </div>

      <div
        className="card stack"
        style={{
          flex: "1 1 180px",
          gap: "var(--s1)",
          ...(isLoss ? { color: "var(--coral-ink)" } : {}),
        }}
      >
        <span className="muted" style={{ fontSize: "12px" }}>
          کل سود ناخالص دوره
        </span>
        <span
          className="bold"
          style={{
            fontSize: "1.3rem",
            color: isLoss ? "var(--coral-ink)" : undefined,
          }}
        >
          {formatMoney(summary.totalProfitRial, "toman")}
        </span>
      </div>

      <div className="card stack" style={{ flex: "1 1 180px", gap: "var(--s1)" }}>
        <span className="muted" style={{ fontSize: "12px" }}>
          سود هر پرس
        </span>
        <span
          className="bold"
          style={{
            fontSize: "1.3rem",
            color: summary.profitPerPortionRial < 0 ? "var(--coral-ink)" : undefined,
          }}
        >
          {formatMoney(summary.profitPerPortionRial, "toman")}
        </span>
      </div>

      <div className="card stack" style={{ flex: "1 1 140px", gap: "var(--s1)" }}>
        <span className="muted" style={{ fontSize: "12px" }}>
          حاشیه سود دوره
        </span>
        <span
          className="bold"
          style={{
            fontSize: "1.3rem",
            color: summary.marginPerMille < 0 ? "var(--coral-ink)" : undefined,
          }}
        >
          {faDigits(summary.marginPerMille)} ‰
        </span>
      </div>
    </div>
  );
}

function OrganizationFoodTable({ rows, summary }: { rows: ProfitFoodRow[]; summary: ProfitPeriodSummary }) {
  if (rows.length === 0) {
    return (
      <Card>
        <p className="muted text-center" style={{ padding: "var(--s6) 0" }}>
          هیچ داده‌ای برای جدول سازمان × غذا در این بازه زمانی یافت نشد.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>سازمان</th>
              <th>غذا / قلم</th>
              <th className="end">تعداد پرس</th>
              <th className="end">فروش</th>
              <th className="end">بهای تمام‌شده</th>
              <th className="end">سود</th>
              <th className="end">سود هر پرس</th>
              <th className="end">حاشیه سود</th>
              <th>وضعیت</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isLoss = r.profitRial !== null && r.profitRial < 0;

              return (
                <tr
                  key={`${r.organizationId}-${r.menuItemId}-${String(i)}`}
                  className={isLoss ? "loss-row" : undefined}
                  style={isLoss ? { color: "var(--coral-ink)" } : undefined}
                >
                  <td className="bold">{r.organizationName}</td>
                  <td>
                    <span>{r.foodName}</span>
                    {r.isSideItem && (
                      <span style={{ marginInlineStart: "6px" }}>
                        <Chip plain tone="blue">
                          جانبی
                        </Chip>
                      </span>
                    )}
                    {r.isSpecialPortion && (
                      <span style={{ marginInlineStart: "6px" }}>
                        <Chip plain tone="warn">
                          پرس ویژه
                        </Chip>
                      </span>
                    )}
                  </td>
                  <td className="end">{faDigits(r.portions)}</td>
                  <td className="end">
                    {r.revenueRial !== null ? formatMoney(r.revenueRial, "toman") : "—"}
                  </td>
                  <td className="end">{r.costRial !== null ? formatMoney(r.costRial, "toman") : "—"}</td>
                  <td className="end bold" style={isLoss ? { color: "var(--coral-ink)" } : undefined}>
                    {r.profitRial !== null ? formatMoney(r.profitRial, "toman") : "—"}
                  </td>
                  <td
                    className="end"
                    style={
                      r.profitPerPortionRial !== null && r.profitPerPortionRial < 0
                        ? { color: "var(--coral-ink)" }
                        : undefined
                    }
                  >
                    {r.profitPerPortionRial !== null ? formatMoney(r.profitPerPortionRial, "toman") : "—"}
                  </td>
                  <td
                    className="end"
                    style={
                      r.marginPerMille !== null && r.marginPerMille < 0
                        ? { color: "var(--coral-ink)" }
                        : undefined
                    }
                  >
                    {r.marginPerMille !== null ? `${faDigits(r.marginPerMille)} ‰` : "—"}
                  </td>
                  <td>
                    <StatusBadge status={r.status} isSpecialPortion={r.isSpecialPortion} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold", borderTop: "2px solid var(--card-line)" }}>
              <td colSpan={2}>جمع کل دوره</td>
              <td className="end">{faDigits(summary.totalPortions)}</td>
              <td className="end">{formatMoney(summary.totalRevenueRial, "toman")}</td>
              <td className="end">{formatMoney(summary.totalCostRial, "toman")}</td>
              <td
                className="end"
                style={summary.totalProfitRial < 0 ? { color: "var(--coral-ink)" } : undefined}
              >
                {formatMoney(summary.totalProfitRial, "toman")}
              </td>
              <td
                className="end"
                style={summary.profitPerPortionRial < 0 ? { color: "var(--coral-ink)" } : undefined}
              >
                {formatMoney(summary.profitPerPortionRial, "toman")}
              </td>
              <td
                className="end"
                style={summary.marginPerMille < 0 ? { color: "var(--coral-ink)" } : undefined}
              >
                {faDigits(summary.marginPerMille)} ‰
              </td>
              <td>
                {summary.hasMissingData && (
                  <Chip plain tone="warn">
                    اقلام ناقص مستثنی
                  </Chip>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

function DailyGrossProfitTable({ days, summary }: { days: ProfitDayRow[]; summary: ProfitPeriodSummary }) {
  if (days.length === 0) {
    return (
      <Card>
        <p className="muted text-center" style={{ padding: "var(--s6) 0" }}>
          هیچ داده‌ای برای نمای روزانه در این بازه زمانی یافت نشد.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>روز هفته</th>
              <th className="end">تعداد پرس</th>
              <th className="end">فروش کل</th>
              <th className="end">بهای تمام‌شده کل</th>
              <th className="end">سود ناخالص</th>
              <th className="end">حاشیه سود</th>
              <th>اقلام ناقص</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const isLoss = d.grossProfitRial < 0;
              const missingCount = d.missingPriceCount + d.missingCostCount;

              return (
                <tr
                  key={d.businessDate}
                  className={isLoss ? "loss-row" : undefined}
                  style={isLoss ? { color: "var(--coral-ink)" } : undefined}
                >
                  <td className="bold">{formatDateShort(d.businessDate)}</td>
                  <td>{formatWeekday(d.businessDate)}</td>
                  <td className="end">{faDigits(d.portions)}</td>
                  <td className="end">{formatMoney(d.revenueRial, "toman")}</td>
                  <td className="end">{formatMoney(d.costRial, "toman")}</td>
                  <td className="end bold" style={isLoss ? { color: "var(--coral-ink)" } : undefined}>
                    {formatMoney(d.grossProfitRial, "toman")}
                  </td>
                  <td
                    className="end"
                    style={d.marginPerMille < 0 ? { color: "var(--coral-ink)" } : undefined}
                  >
                    {faDigits(d.marginPerMille)} ‰
                  </td>
                  <td>
                    {missingCount > 0 ? (
                      <Chip plain tone="warn">
                        {faDigits(missingCount)} قلم ناقص
                      </Chip>
                    ) : (
                      <Chip plain tone="ok">
                        کامل
                      </Chip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold", borderTop: "2px solid var(--card-line)" }}>
              <td colSpan={2}>جمع کل روزها</td>
              <td className="end">{faDigits(summary.totalPortions)}</td>
              <td className="end">{formatMoney(summary.totalRevenueRial, "toman")}</td>
              <td className="end">{formatMoney(summary.totalCostRial, "toman")}</td>
              <td
                className="end"
                style={summary.totalProfitRial < 0 ? { color: "var(--coral-ink)" } : undefined}
              >
                {formatMoney(summary.totalProfitRial, "toman")}
              </td>
              <td
                className="end"
                style={summary.marginPerMille < 0 ? { color: "var(--coral-ink)" } : undefined}
              >
                {faDigits(summary.marginPerMille)} ‰
              </td>
              <td>—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

function OrganizationSummariesGrid({ summaries }: { summaries: OrganizationProfitSummary[] }) {
  return (
    <div className="stack" style={{ gap: "var(--s3)" }}>
      <h2 className="t-title" style={{ fontSize: "1.1rem", margin: 0 }}>
        خلاصه سود سازمان‌ها در دوره
      </h2>
      <div className="row wrap" style={{ gap: "var(--s4)" }}>
        {summaries.map((s) => {
          const isLoss = s.totalProfitRial < 0;

          return (
            <div
              key={s.organizationId}
              className="card stack"
              style={{
                flex: "1 1 280px",
                gap: "var(--s2)",
                ...(isLoss ? { borderColor: "var(--coral-soft)" } : {}),
              }}
            >
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <span className="bold">{s.organizationName}</span>
                {s.hasMissingData && (
                  <Chip plain tone="warn">
                    داده ناقص
                  </Chip>
                )}
              </div>

              <div className="row" style={{ justifyContent: "space-between", fontSize: "13px" }}>
                <span className="muted">تعداد پرس:</span>
                <span>{faDigits(s.totalPortions)}</span>
              </div>

              <div className="row" style={{ justifyContent: "space-between", fontSize: "13px" }}>
                <span className="muted">فروش:</span>
                <span>{formatMoney(s.totalRevenueRial, "toman")}</span>
              </div>

              <div className="row" style={{ justifyContent: "space-between", fontSize: "13px" }}>
                <span className="muted">بهای تمام‌شده:</span>
                <span>{formatMoney(s.totalCostRial, "toman")}</span>
              </div>

              <div
                className="row bold"
                style={{
                  justifyContent: "space-between",
                  fontSize: "14px",
                  color: isLoss ? "var(--coral-ink)" : undefined,
                }}
              >
                <span>سود:</span>
                <span>{formatMoney(s.totalProfitRial, "toman")}</span>
              </div>

              <div className="row" style={{ justifyContent: "space-between", fontSize: "13px" }}>
                <span className="muted">سود هر پرس:</span>
                <span style={s.profitPerPortionRial < 0 ? { color: "var(--coral-ink)" } : undefined}>
                  {formatMoney(s.profitPerPortionRial, "toman")}
                </span>
              </div>

              <div className="row" style={{ justifyContent: "space-between", fontSize: "13px" }}>
                <span className="muted">حاشیه سود:</span>
                <span style={s.marginPerMille < 0 ? { color: "var(--coral-ink)" } : undefined}>
                  {faDigits(s.marginPerMille)} ‰
                </span>
              </div>

              {s.specialPortionsCostRial > 0 && (
                <div
                  className="row"
                  style={{
                    justifyContent: "space-between",
                    fontSize: "12px",
                    color: "var(--warn-ink)",
                    paddingTop: "var(--s1)",
                    borderTop: "1px dashed var(--card-line)",
                  }}
                >
                  <span>هزینه‌ی پرس ویژه (سهم آشپزخانه):</span>
                  <span>{formatMoney(s.specialPortionsCostRial, "toman")}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({
  status,
  isSpecialPortion,
}: {
  status: ProfitFoodRow["status"];
  isSpecialPortion?: boolean | undefined;
}) {
  if (isSpecialPortion) {
    return (
      <Chip plain tone="coral">
        هزینه‌ی پرس ویژه
      </Chip>
    );
  }

  switch (status) {
    case "ok":
      return (
        <Chip plain tone="ok">
          تکمیل
        </Chip>
      );
    case "missing_price":
      return (
        <Chip plain tone="warn">
          بدون قیمت
        </Chip>
      );
    case "missing_cost":
      return (
        <Chip plain tone="warn">
          بدون بهای تمام‌شده
        </Chip>
      );
    case "missing_both":
      return (
        <Chip plain tone="warn">
          بدون قیمت و بها
        </Chip>
      );
  }
}
